import { setUser as sentrySetUser } from '@sentry/react';
import CustomModal from '../../components/custom-modal';
import { Container } from '@mantine/core';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ILanguage, fetchLanguages } from '../../../libraries/language';
import { fetchRegions } from '../../../libraries/region';
import { IUser, PermissionOption, fetchUser, fetchPermissions } from '../../../libraries/user';
import { AppState } from '../../client-redux/reducers';
import { IEntity } from '../../../libraries/entity';
import Header from '../../components/util/header';
import { Language } from '../../languages/languageHandler';
import { preparePermissions } from '../users/permissions';
import { selectedLang } from '../../client-redux/languages';
import UserForm, { UserFormMode } from '../users/user-form';
import { AppDispatch } from '../../client-redux/store';
import './styles.scss';

const Profile = () => {
  const loginInfo = useSelector<AppState, IUser>(state => state.login.user);
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const entities = useSelector<AppState, IEntity[]>(state => state.entities.entities);
  const dispatch = useDispatch<AppDispatch>();
  const [user, setUser] = useState<IUser | null>(null);

  const [permissions, setPermissions] = useState<PermissionOption[]>([]);
  const [, setRegions] = useState<string[]>([]);
  const [, setLanguages] = useState<ILanguage[]>([]);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const loadProfileData = async () => {
      try {
        const response = await fetchUser(loginInfo.entid, loginInfo.uid ?? 0);

        if (response.statusOper.code === 0 && response.users?.length) {
          const fetchedUser = response.users[0];
          setUser(fetchedUser);
          sentrySetUser(fetchedUser);

          const [permResponse, regionResponse, langResponse] = await Promise.all([
            fetchPermissions(),
            fetchRegions(),
            fetchLanguages()
          ]);

          preparePermissions(permResponse.list, fetchedUser);
          setPermissions(permResponse.list);
          setRegions(regionResponse.regions);
          setLanguages(langResponse.languages);
        } else {
          setMessage(lang.EDIT_USER_ALERT_UPDATE_ERROR || 'Failed to load user data.');
          setShowErrorDialog(true);
        }
      } catch (err) {
        console.error('Error fetching user profile:', err);
        setMessage(lang.EDIT_USER_ALERT_UPDATE_ERROR || 'Error loading user data.');
        setShowErrorDialog(true);
      }
    };

    loadProfileData();
  }, [loginInfo, lang]);

  return (
    <Container fluid className="profile">
      <Header pageTitle={lang.USER_MY_PROFILE} />

      {user && (
        <UserForm
          mode={UserFormMode.UPDATE_PROFILE}
          isNew={false}
          user={user}
          lang={lang}
          permissionOptions={permissions}
          togglePermissions={() => {}}
          onSuccess={() => {
            setMessage(lang.EDIT_USER_ALERT_UPDATE_SUCCESS || 'Profile updated successfully');
            setShowSuccessDialog(true);
            setTimeout(() => setShowSuccessDialog(false), 200000);
          }}
          onError={msg => {
            setMessage(`${lang.EDIT_USER_ALERT_UPDATE_ERROR}\n${msg}`);
            setShowErrorDialog(true);
            setTimeout(() => setShowErrorDialog(false), 200000);
          }}
          entities={entities}
          changeLanguage={(langId: number) => dispatch(selectedLang(langId))}
        />
      )}

      {/* Feedback modals */}
      <CustomModal
        opened={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        message={message}
        backgroundColor="#3BDF70"
        textColor="black"
      />
      <CustomModal
        opened={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        message={message}
        backgroundColor="#E3353F"
        textColor="white"
      />
    </Container>
  );
};

export default Profile;
